// Uid -> display-name lookup. Backs any screen that would otherwise show a
// raw Firebase uid (submittedBy / attestedBy / provisionedBy /
// peerReviewerUid) — those fields carry no persisted profile for
// platform_admin actors (see apps/admin/src/routes/identities.ts). Never use
// this for same-tenant member lookups — useTenantUsersList already has
// displayName/email for those and needs no extra round trip.
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useApi } from "./api";
import { useTenantUsersList } from "./tenantsQuery";

export type ResolvedIdentity = {
  readonly uid: string;
  readonly email: string;
  readonly displayName?: string;
};

export type IdentitiesResponse = {
  identities: ResolvedIdentity[];
};

const IDENTITIES_KEY = ["identities"] as const;

/** uid -> best available label (displayName, else email). Missing entries mean unresolved. */
export type IdentityLabels = ReadonlyMap<string, string>;

// Sorted+deduped uids keep the query key (and thus the cache entry) stable
// across renders that pass the same set in a different order.
function normalizeUids(uids: readonly string[]): string[] {
  return Array.from(new Set(uids.filter((uid) => uid.length > 0))).sort();
}

export function useIdentityLabels(
  uids: readonly string[]
): UseQueryResult<IdentityLabels, Error> {
  const api = useApi("admin");
  const normalized = normalizeUids(uids);
  return useQuery({
    queryKey: [...IDENTITIES_KEY, normalized],
    queryFn: async () => {
      const res = await api<IdentitiesResponse>(
        `/identities?uids=${normalized.map(encodeURIComponent).join(",")}`
      );
      const labels = new Map<string, string>();
      for (const identity of res.identities) {
        labels.set(identity.uid, identity.displayName ?? identity.email);
      }
      return labels;
    },
    enabled: normalized.length > 0,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * uid -> label for a page whose actor uids are a mix of platform_admin
 * (project-level Identity Platform, e.g. an L1 template's submittedBy) and
 * members of ONE known tenant (L2 rows, all belonging to the caller's own
 * studio — a tenant_admin never browses another studio's L2 content). The
 * tenant roster (Firestore-persisted, immune to Identity Platform's
 * per-studio auth partitioning — see S-294a) wins; the platform lookup
 * fills in the rest. Pass `tenantId: ""` when the page is L1-only.
 */
export function useActorLabels(
  uids: readonly string[],
  tenantId: string
): IdentityLabels {
  const tenantUsers = useTenantUsersList(tenantId).data?.users ?? [];
  const platformLabels = useIdentityLabels(uids).data;
  const merged = new Map<string, string>(platformLabels ?? []);
  for (const u of tenantUsers) {
    merged.set(u.uid, u.displayName ?? u.email);
  }
  return merged;
}
